// System Maintenance Page — shown platform-wide when maintenance mode is enabled.
import React, { useEffect, useState } from 'react';

interface Props {
  reason?: string;
  estimatedEnd?: string | null;
  services?: string[];
  logoUrl?: string | null;
  siteName?: string;
}

const SystemMaintenancePage: React.FC<Props> = ({ reason, estimatedEnd, logoUrl, siteName }) => {
  const [dots, setDots] = useState('.');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(e => e + 1);
      setDots(d => d.length >= 3 ? '.' : d + '.');
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const formatEstimated = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('th-TH', {
        dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Bangkok',
      });
    } catch { return iso; }
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center position-relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)' }}
    >
      <style>{`
        @keyframes spin-gear  { to { transform: rotate(360deg); } }
        @keyframes pulse-ring { 0%,100% { transform: scale(1); opacity:.35; } 50% { transform: scale(1.18); opacity:.1; } }
        @keyframes float-card { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes fade-in    { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      <div className="container" style={{ maxWidth: 520, zIndex: 10, position: 'relative' }}>

        {/* Tenant Logo */}
        {logoUrl && (
          <div
            className="text-center mb-4"
            style={{ animation: 'fade-in 0.6s ease forwards' }}
          >
            <img
              src={logoUrl}
              alt={siteName || 'Logo'}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              style={{
                maxHeight: '72px',
                maxWidth: '200px',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 12px rgba(99,102,241,0.4))',
                borderRadius: '8px',
              }}
            />
            {siteName && (
              <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px', letterSpacing: '0.03em' }}>
                {siteName}
              </p>
            )}
          </div>
        )}

        {/* Gear icon */}
        <div className="text-center mb-4 position-relative d-inline-block" style={{ left: '50%', transform: 'translateX(-50%)' }}>
          <svg
            width="80" height="80" viewBox="0 0 24 24" fill="none"
            style={{ animation: 'spin-gear 6s linear infinite' }}
          >
            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z"
              stroke="#818cf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
              stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
          <span style={{
            position: 'absolute', inset: '-10px', borderRadius: '50%',
            border: '2px solid #6366f1',
            animation: 'pulse-ring 2s ease-in-out infinite',
          }} />
        </div>

        {/* Heading */}
        <h1 className="text-center fw-bold mb-1" style={{ fontSize: 'clamp(20px,5vw,30px)', color: '#c7d2fe' }}>
          🔧 ปิดปรับปรุงชั่วคราว
        </h1>
        <h2 className="text-center fw-normal mb-4" style={{ fontSize: 'clamp(13px,3vw,18px)', color: '#94a3b8' }}>
          Temporarily Under Maintenance
        </h2>

        {/* Card */}
        <div
          className="p-4 text-center"
          style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(99,102,241,0.3)',
            borderRadius: '16px',
            animation: 'float-card 4s ease-in-out infinite',
          }}
        >
          <p style={{ color: '#cbd5e1', lineHeight: 1.8 }}>
            ขณะนี้ระบบอยู่ระหว่างการปรับปรุง
          </p>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.8 }}>
            Our system is currently undergoing scheduled maintenance.
            <br />We apologize for the inconvenience.
          </p>

          {reason && (
            <div
              className="mt-3 mb-3 px-3 py-2"
              style={{
                background: 'rgba(99,102,241,0.1)',
                border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: '10px',
                fontSize: '14px',
                color: '#a5b4fc',
              }}
            >
              {reason}
            </div>
          )}

          {estimatedEnd && (
            <p style={{ fontSize: '13px', color: '#64748b' }}>
              คาดว่าจะเปิดให้บริการอีกครั้ง:{' '}
              <strong style={{ color: '#818cf8' }}>{formatEstimated(estimatedEnd)}</strong>
            </p>
          )}

          <p className="mt-3" style={{ fontSize: '14px', color: '#475569' }}>
            กรุณารอสักครู่{dots}
            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#334155' }}>
              ({elapsed}s)
            </span>
          </p>
        </div>

        <p className="text-center mt-4" style={{ fontSize: '13px', color: '#334155' }}>
          ขออภัยในความไม่สะดวก · We appreciate your patience
        </p>
      </div>
    </div>
  );
};

export default SystemMaintenancePage;
